import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { submitPaper } from '../services/api';
import { getJournals } from '../services/api';
import { CheckCircle, Upload, AlertCircle, ChevronRight } from 'lucide-react';

const STEPS = ['Paper Information', 'Author Details', 'Review & Submit'];

const SubmitPaperPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submissionId, setSubmissionId] = useState('');
  const [error, setError] = useState('');
  const [fileName, setFileName] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [journals, setJournals] = useState([]);

  useEffect(() => {
    getJournals()
      .then(setJournals)
      .catch(() => setJournals([]));
  }, []);
  
  const [form, setForm] = useState({
    title: '',
    abstract: '',
    keywords: '',
    journalId: '',
    authorName: user?.name || '',
    email: user?.email || '',
    affiliation: user?.affiliation || '',
    coAuthors: '',
    coverLetter: '',
  });

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setError("Only PDF files are allowed");
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("File size must be under 20MB");
      return;
    }
    setFileName(file.name);
    setSelectedFile(file);
  };

  const validateStep = () => {
    if (step === 0) {
      if (!form.title.trim()) { setError('Paper title is required.'); return false; }
      if (!form.abstract.trim() || form.abstract.length < 100) { setError('Abstract must be at least 100 characters.'); return false; }
      if (!form.keywords.trim()) { setError('At least one keyword is required.'); return false; }
      if (!form.journalId) { setError('Please select a target journal.'); return false; }
    }
    if (step === 1) {
      if (!form.authorName.trim()) { setError('Author name is required.'); return false; }
      if (!form.email.trim()) { setError('Email address is required.'); return false; }
      if (!form.affiliation.trim()) { setError('Institutional affiliation is required.'); return false; }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep()) { setStep(s => s + 1); setError(''); }
  };

  const prevStep = () => { setStep(s => s - 1); setError(''); };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    if (!selectedFile) {
      setError("PDF file is required");
      setLoading(false);
      return;
    }

    try {
      const selectedJournal = journals.find(j => j.id === form.journalId);

      // =========================
      // STEP 1 → Upload PDF
      // =========================
      const fd = new FormData();
      fd.append("file", selectedFile);

      const uploadRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/submissions/upload`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          body: fd,
        }
      );

      // ✅ Check ok FIRST — prevents "body stream already read" error
      if (!uploadRes.ok) {
        let errDetail = `Upload failed (${uploadRes.status})`;
        try {
          const errData = await uploadRes.json();
          errDetail = errData.detail || errDetail;
        } catch {
          const errText = await uploadRes.text().catch(() => "");
          if (errText) errDetail = errText.slice(0, 200);
        }
        throw new Error(errDetail);
      }

      const uploadData = await uploadRes.json();

      if (!uploadData.fileUrl || !uploadData.filePath) {
        throw new Error("File upload incomplete");
      }

      // =========================
      // STEP 2 → Submit JSON
      // =========================
      const result = await submitPaper({
        title: form.title,
        abstract: form.abstract,
        keywords: JSON.stringify(
          (form.keywords || "")
            .split(',')
            .map(k => k.trim())
            .filter(Boolean)
        ),
        journalId: form.journalId,
        journalTitle: selectedJournal?.title || "",
        authorName: form.authorName,
        authorEmail: form.email,
        affiliation: form.affiliation,
        coAuthors: form.coAuthors || "",
        coverLetter: form.coverLetter || "",
        authorId: user?.id,
        fileUrl: uploadData.fileUrl,
        filePath: uploadData.filePath,
      });

      setSubmissionId(result?.id || "N/A");
      setSubmitted(true);

    } catch (err) {
      console.error(err);
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F5F7FA] flex items-center justify-center py-12 px-4" data-testid="submission-success">
        <div className="max-w-md w-full bg-white border border-[#E5E7EB] p-10 text-center">
          <div className="w-14 h-14 bg-green-100 border border-green-200 flex items-center justify-center mx-auto mb-5">
            <CheckCircle size={28} className="text-green-600" />
          </div>
          <h2 className="font-serif text-2xl font-semibold text-[#1F1F1F] mb-2">Submission Received</h2>
          <p className="text-[#6B7280] text-sm mb-5 leading-relaxed">
            Your manuscript has been successfully submitted. You will receive a confirmation email within 24 hours.
          </p>
          <div className="bg-[#F5F7FA] border border-[#E5E7EB] px-5 py-3 mb-6">
            <p className="text-xs text-[#6B7280]">Submission ID</p>
            <p className="font-mono text-sm font-semibold text-[#1A73E8]">{submissionId}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/dashboard/author')}
              className="flex-1 py-2.5 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors"
              data-testid="go-to-dashboard-btn"
            >
              View Dashboard
            </button>
            <button
              onClick={() => { setSubmitted(false); setStep(0); setForm({ title: '', abstract: '', keywords: '', journalId: '', authorName: user?.name || '', email: user?.email || '', affiliation: user?.affiliation || '', coAuthors: '', coverLetter: '' }); setFileName(''); }}
              className="flex-1 py-2.5 border border-[#E5E7EB] text-sm text-[#374151] hover:bg-[#F5F7FA] transition-colors"
            >
              New Submission
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F5F7FA] min-h-screen" data-testid="submit-paper-page">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
          <p className="section-overline mb-2">Paper Submission</p>
          <h1 className="font-serif text-3xl font-semibold text-[#1F1F1F]">Submit Your Manuscript</h1>
          <p className="text-[#6B7280] text-sm mt-2">Complete all three steps to submit your research paper for peer review.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center mb-8" data-testid="step-indicator">
          {STEPS.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center text-sm font-semibold border-2 ${
                  i < step ? 'step-completed' : i === step ? 'step-active' : 'step-inactive'
                }`}>
                  {i < step ? <CheckCircle size={14} /> : i + 1}
                </div>
                <span className={`text-sm font-medium hidden sm:block ${i === step ? 'text-[#1A73E8]' : i < step ? 'text-[#174EA6]' : 'text-[#9CA3AF]'}`}>
                  {label}
                </span>
              </div>
              {i < 2 && <div className={`flex-1 h-0.5 mx-3 ${i < step ? 'step-line-active' : 'step-line-inactive'}`} />}
            </React.Fragment>
          ))}
        </div>

        <div className="bg-white border border-[#E5E7EB]">
          <div className="px-6 py-4 bg-[#F5F7FA] border-b border-[#E5E7EB]">
            <h2 className="font-semibold text-[#1F1F1F] text-base">Step {step + 1}: {STEPS[step]}</h2>
          </div>
          <div className="p-6 sm:p-8">
            {error && (
              <div className="flex items-start gap-2.5 p-3 mb-5 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="submit-error">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" /> {error}
              </div>
            )}

            {step === 0 && (
              <div className="space-y-6" data-testid="step-0-form">
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Paper Title <span className="text-red-500">*</span>
                  </label>
                  <input name="title" value={form.title} onChange={handleChange}
                    placeholder="Enter the full title of your manuscript"
                    className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8]"
                    data-testid="paper-title-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Abstract <span className="text-red-500">*</span>
                    <span className="text-[#9CA3AF] font-normal ml-1">(min. 100 characters)</span>
                  </label>
                  <textarea name="abstract" value={form.abstract} onChange={handleChange} rows={6}
                    placeholder="Provide a concise summary of your research, including objectives, methodology, results, and conclusions..."
                    className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] resize-none"
                    data-testid="paper-abstract-input" />
                  <p className="text-xs text-[#9CA3AF] mt-1">{form.abstract.length} characters</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Keywords <span className="text-red-500">*</span>
                    <span className="text-[#9CA3AF] font-normal ml-1">(comma-separated)</span>
                  </label>
                  <input name="keywords" value={form.keywords} onChange={handleChange}
                    placeholder="e.g., quantum computing, decoherence, entanglement"
                    className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8]"
                    data-testid="paper-keywords-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">
                    Target Journal <span className="text-red-500">*</span>
                  </label>
                  <select name="journalId" value={form.journalId} onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] bg-white focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] appearance-none"
                    data-testid="paper-journal-select">
                    <option value="">-- Select a journal --</option>
                    {journals.map(j => <option key={j.id} value={j.id}>{j.shortTitle} — {j.title}</option>)}
                  </select>
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-5" data-testid="step-1-form">
                {[
                  { name: 'authorName', label: 'Corresponding Author Name', placeholder: 'Dr. Full Name', required: true },
                  { name: 'email', label: 'Contact Email', placeholder: 'author@institution.edu', required: true },
                  { name: 'affiliation', label: 'Institutional Affiliation', placeholder: 'University / Research Center Name', required: true },
                  { name: 'coAuthors', label: 'Co-Authors', placeholder: 'Name 1 (Institution); Name 2 (Institution)', required: false },
                ].map(field => (
                  <div key={field.name}>
                    <label className="block text-sm font-medium text-[#374151] mb-1.5">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    <input name={field.name} value={form[field.name]} onChange={handleChange}
                      placeholder={field.placeholder}
                      className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8]"
                      data-testid={`author-${field.name}-input`} />
                  </div>
                ))}
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">Upload Manuscript (PDF)</label>
                  <label className="flex items-center justify-center gap-3 px-4 py-6 border-2 border-dashed border-[#E5E7EB] cursor-pointer hover:border-[#1A73E8] hover:bg-[#F5F7FA] transition-colors" data-testid="file-upload-area">
                    <Upload size={18} className="text-[#9CA3AF]" />
                    <div className="text-sm">
                      {fileName ? (
                        <span className="text-[#1A73E8] font-medium">{fileName}</span>
                      ) : (
                        <span className="text-[#6B7280]">Click to upload PDF (max 20MB)</span>
                      )}
                    </div>
                    <input type="file" accept=".pdf,.doc,.docx" onChange={handleFileChange} className="hidden" data-testid="file-input" />
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#374151] mb-1.5">Cover Letter <span className="text-[#9CA3AF] font-normal">(optional)</span></label>
                  <textarea name="coverLetter" value={form.coverLetter} onChange={handleChange} rows={4}
                    placeholder="Address to the editor-in-chief, briefly describing the significance of your work..."
                    className="w-full px-4 py-2.5 border border-[#E5E7EB] text-sm text-[#1F1F1F] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#1A73E8] focus:border-[#1A73E8] resize-none"
                    data-testid="cover-letter-input" />
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="step-2-review">
                <p className="text-sm text-[#6B7280] mb-6">Please review your submission details before final submission.</p>
                <div className="space-y-4">
                  {[
                    { section: 'Paper Information', items: [
                      { label: 'Title', value: form.title },
                      { label: 'Abstract', value: form.abstract.substring(0, 150) + (form.abstract.length > 150 ? '...' : '') },
                      { label: 'Keywords', value: form.keywords },
                      { label: 'Journal', value: journals.find(j => j.id === form.journalId)?.shortTitle + ' — ' + journals.find(j => j.id === form.journalId)?.title || '' },
                    ]},
                    { section: 'Author Details', items: [
                      { label: 'Author', value: form.authorName },
                      { label: 'Email', value: form.email },
                      { label: 'Affiliation', value: form.affiliation },
                      { label: 'Co-Authors', value: form.coAuthors || '—' },
                    ]},
                    { section: 'Files', items: [
                      { label: 'Manuscript', value: fileName || 'No file selected' },
                    ]},
                  ].map(section => (
                    <div key={section.section} className="border border-[#E5E7EB]">
                      <div className="px-4 py-2.5 bg-[#F5F7FA] border-b border-[#E5E7EB]">
                        <h4 className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{section.section}</h4>
                      </div>
                      <div className="p-4 space-y-2">
                        {section.items.map(item => (
                          <div key={item.label} className="grid grid-cols-4 text-sm gap-2">
                            <span className="text-[#6B7280] col-span-1">{item.label}:</span>
                            <span className="text-[#1F1F1F] col-span-3 break-words">{item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-6 p-4 bg-[#F5F7FA] border border-[#E5E7EB] text-xs text-[#6B7280] leading-relaxed">
                  <strong className="text-[#374151]">Declaration:</strong> By submitting this manuscript, I confirm that the work is original, has not been published elsewhere, and is not under consideration by another journal. All authors have approved the manuscript for submission.
                </div>
                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[
                    { title: 'AI Abstract Summarizer', desc: 'Auto-generate structured summaries' },
                    { title: 'AI Quality Check', desc: 'Detect plagiarism and assess quality' },
                    { title: 'AI Reviewer Match', desc: 'Match paper with expert reviewers' },
                  ].map(ai => (
                    <div key={ai.title} className="border border-[#E5E7EB] p-3 bg-[#F9FAFB] opacity-70">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-xs font-semibold text-[#374151]">{ai.title}</p>
                        <span className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 border border-blue-200 font-medium">Coming Soon</span>
                      </div>
                      <p className="text-xs text-[#9CA3AF]">{ai.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="px-6 pb-6 sm:px-8 sm:pb-8 flex items-center justify-between border-t border-[#E5E7EB] pt-5">
            <button
              onClick={prevStep}
              disabled={step === 0}
              className="px-5 py-2.5 border border-[#E5E7EB] text-sm text-[#374151] hover:bg-[#F5F7FA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="prev-step-btn"
            >
              Previous
            </button>
            {step < 2 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-1.5 px-6 py-2.5 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors"
                data-testid="next-step-btn"
              >
                Continue <ChevronRight size={15} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="px-8 py-2.5 bg-[#1A73E8] text-white text-sm font-semibold hover:bg-[#174EA6] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="final-submit-btn"
              >
                {loading ? 'Submitting...' : 'Submit Manuscript'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitPaperPage;
